// src/admin/components/PdfPreviewModal.jsx
import React, { useState } from "react";
import Modal from "./Modal";
import { Download, ExternalLink, FileText, ZoomIn, ZoomOut } from "lucide-react";

const PdfPreviewModal = ({ isOpen, onClose, pdfUrl, title = "Document Preview" }) => {
  const [zoom, setZoom] = useState(100);

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 15, 160));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 15, 70));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle="PDF Document Viewer & Study Material Reader"
      maxWidth="max-w-4xl"
      footer={
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <span>Zoom: {zoom}%</span>
            <button
              onClick={handleZoomOut}
              className="p-1 hover:bg-slate-200 rounded text-slate-700"
              title="Zoom out"
            >
              <ZoomOut size={15} />
            </button>
            <button
              onClick={handleZoomIn}
              className="p-1 hover:bg-slate-200 rounded text-slate-700"
              title="Zoom in"
            >
              <ZoomIn size={15} />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={pdfUrl || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
            >
              <ExternalLink size={14} /> Open in New Tab
            </a>
            <a
              href={pdfUrl || "#"}
              download
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg bg-red-900 hover:bg-red-800 text-white shadow-sm transition-colors"
            >
              <Download size={14} /> Download PDF
            </a>
          </div>
        </div>
      }
    >
      <div className="bg-slate-900/90 rounded-xl p-4 min-h-[500px] flex flex-col items-center justify-center text-white relative overflow-hidden">
        {/* If valid PDF iframe can be rendered or mock fallback */}
        {pdfUrl && !pdfUrl.startsWith("blob:") ? (
          <iframe
            src={pdfUrl}
            title={title}
            className="w-full h-[520px] rounded-lg border-0 bg-white"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-center p-8 max-w-md">
            <div className="w-16 h-16 rounded-2xl bg-red-900/40 border border-red-500/30 flex items-center justify-center mb-4 text-red-400">
              <FileText size={32} />
            </div>
            <h4 className="text-base font-bold text-white mb-1">{title}</h4>
            <p className="text-xs text-slate-300 mb-6">
              Previewing uploaded document session. In this demo environment, files uploaded in the browser are managed via local memory and object URLs.
            </p>
            <div className="w-full bg-slate-800/80 rounded-xl p-4 border border-slate-700 text-left text-xs space-y-2 mb-4">
              <div className="flex justify-between text-slate-400">
                <span>File Format:</span>
                <span className="font-mono text-emerald-400">PDF (Adobe Acrobat)</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Page Count:</span>
                <span className="font-semibold text-white">48 Pages</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Optimized For:</span>
                <span className="text-amber-400">UPSC CSE Prelims / Mains</span>
              </div>
            </div>
            <a
              href={pdfUrl || "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf"}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-800 hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-lg transition-all"
            >
              <Download size={14} /> Open Sample Document
            </a>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default PdfPreviewModal;
